import React from 'react';
import { Row, Col, Card, ProgressBar, Table, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignal, faCheckCircle, faTimesCircle, faExclamationTriangle, faMicrochip } from '@fortawesome/free-solid-svg-icons';

const DeviceHealthSection = ({ 
  selectedReport, 
  sectionVisibility, 
  onHandleShopListModal 
}) => {
  if (!sectionVisibility.deviceHealth) return null;

  const handleDevicesInWrongShopsModal = (title, devices) => {
    onHandleShopListModal(title, devices);
  };

  return (
    <>
      {/* Device Communication Status */}
      <Row className="mb-4">
        <Col md={selectedReport.data?.devicesInWrongShops?.length > 0 ? 7 : 12}>
          <Card className="shadow-sm report-card">
            <Card.Header className="bg-light">
              <h6 className="mb-0">
                <FontAwesomeIcon icon={faSignal} className="me-2" />
                Status komunikacji urządzeń
              </h6>
            </Card.Header>
            <Card.Body>
              <Row className="align-items-center">
                <Col md={8}>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="fw-bold text-success">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                        Urządzenia odpowiadające
                      </span>
                      <span className="fw-bold">
                        {selectedReport.data?.totalAuthDevicesResponding || 0} / {selectedReport.data?.totalAuthDevices || 0}
                      </span>
                    </div>
                    <ProgressBar 
                      now={selectedReport.data?.totalAuthDevices ? 
                        (selectedReport.data.totalAuthDevicesResponding / selectedReport.data.totalAuthDevices) * 100 : 0} 
                      variant="success" 
                      style={{height: '12px'}}
                      className="mb-3"
                    />
                    
                    <div className="d-flex justify-content-between mb-2">
                      <span className="fw-bold text-danger">
                        <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                        Urządzenia nieodpowiadające
                      </span>
                      <span className="fw-bold">
                        {selectedReport.data?.totalAuthDevicesNotResponding || 0} / {selectedReport.data?.totalAuthDevices || 0}
                      </span>
                    </div>
                    <ProgressBar 
                      now={selectedReport.data?.totalAuthDevices ? 
                        (selectedReport.data.totalAuthDevicesNotResponding / selectedReport.data.totalAuthDevices) * 100 : 0} 
                      variant="danger" 
                      style={{height: '12px'}}
                    />
                  </div>
                </Col>
                <Col md={4} className="text-center">
                  <div className="border rounded p-3">
                    <h2 className="mb-1 text-primary">{selectedReport.data?.totalAuthDevices || 0}</h2>
                    <p className="text-muted mb-0 small">Łączna liczba<br />urządzeń</p>
                    <hr className="my-2" />
                    <div className="small">
                      <div className="text-success mb-1">
                        {selectedReport.data?.totalAuthDevices ? 
                          ((selectedReport.data.totalAuthDevicesResponding / selectedReport.data.totalAuthDevices) * 100).toFixed(1) : 0}% sprawnych
                      </div>
                      <div className="text-danger">
                        {selectedReport.data?.totalAuthDevices ? 
                          ((selectedReport.data.totalAuthDevicesNotResponding / selectedReport.data.totalAuthDevices) * 100).toFixed(1) : 0}% problemów
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Devices in Wrong Shops Section */}
        {selectedReport.data?.devicesInWrongShops?.length > 0 && (
          <Col md={5}>
            <Card className="shadow-sm report-card border-warning h-100">
              <Card.Header className="bg-warning text-dark">
                <h6 className="mb-0">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  Urządzenia w niewłaściwych drogeriach
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="text-center mb-3">
                  <div className="border border-warning rounded p-3 bg-light">
                    <h2 className="mb-1 text-warning">{selectedReport.data.devicesInWrongShops.length}</h2>
                    <div className="small text-muted text-center">
                      Urządzenia których adres IP nie pokrywa się z adresami IP przydzielanymi w drogerii do której są dodane
                    </div>
                  </div>
                </div>
               
                <div className="d-grid">
                  <Button 
                    variant="outline-warning" 
                    size="sm"
                    onClick={() => handleDevicesInWrongShopsModal(
                      'Urządzenia w niewłaściwych drogeriach', 
                      selectedReport.data.devicesInWrongShops
                    )}
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    Zobacz szczegóły
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* Device Statistics by Type & Shop Distribution */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="shadow-sm report-card">
            <Card.Header className="bg-light">
              <h6 className="mb-0">
                <FontAwesomeIcon icon={faSignal} className="me-2" />
                Rozkład urządzeń w drogeriach
              </h6>
            </Card.Header>
            <Card.Body>
              <div style={{maxHeight: '350px', overflowY: 'auto'}}>
                <Table striped hover size="sm" className="mb-0">
                  <thead className="sticky-top bg-white">
                    <tr>
                      <th width="15%">Urządzenia</th>
                      <th width="15%">Drogerie</th>
                      <th width="15%">Procent</th>
                      <th width="35%">Wizualizacja</th>
                      <th width="20%">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedReport.data?.shopsDevicesCountStatistics?.slice(0, 15).map((stat, index) => {
                      const totalShops = selectedReport.data?.totalShops || 1;
                      const percentage = ((stat.count / totalShops) * 100).toFixed(1);
                      const getRiskLevel = (deviceCount) => {
                        if (deviceCount === 0) return { color: 'danger', text: 'KRYTYCZNE', icon: '🚨' };
                        if (deviceCount <= 2) return { color: 'warning', text: 'RYZYKO', icon: '⚠️' };
                        if (deviceCount >= 3) return { color: 'success', text: 'BEZPIECZNE', icon: '✅' };
                        return { color: 'secondary', text: 'NIEZNANE', icon: '❓' };
                      };
                      const risk = getRiskLevel(stat.no);
                      
                      return (
                        <tr key={index}>
                          <td>
                            <Badge 
                              bg={risk.color} 
                              className="px-2 py-1 clickable-badge"
                              style={{cursor: 'pointer'}}
                              onClick={() => onHandleShopListModal(
                                `Drogerie z ${stat.no} urządzeniami`, 
                                stat.shops || [],
                                `${stat.no} urządzeń`
                              )}
                            >
                              {stat.no}
                            </Badge>
                          </td>
                          <td>
                            <strong 
                              className="clickable-badge text-primary"
                              style={{cursor: 'pointer'}}
                              onClick={() => onHandleShopListModal(
                                `Drogerie z ${stat.no} urządzeniami`, 
                                stat.shops || [],
                                `${stat.no} urządzeń`
                              )}
                            >
                              {stat.count}
                            </strong>
                          </td>
                          <td>{percentage}%</td>
                          <td>
                            <ProgressBar 
                              now={parseFloat(percentage)} 
                              variant={risk.color}
                              style={{height: '8px'}}
                              className="w-100"
                            />
                          </td>
                          <td>
                            <Badge 
                              bg={risk.color} 
                              className="small clickable-badge"
                              style={{cursor: 'pointer'}}
                              onClick={() => onHandleShopListModal(
                                `Drogerie z ${stat.no} urządzeniami - ${risk.text.toLowerCase()}`, 
                                stat.shops || [],
                                `${stat.no} urządzeń - ${risk.text}`
                              )}
                            >
                              {risk.icon} {risk.text}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                
                {/* Summary Stats */}
                <div className="mt-3 p-2 bg-light rounded">
                  <Row className="text-center small">
                    <Col xs={4}>
                      <div 
                        className="text-danger fw-bold clickable-badge"
                        style={{cursor: 'pointer'}}
                        onClick={() => onHandleShopListModal(
                          'Drogerie bez urządzeń - KRYTYCZNE', 
                          selectedReport.data?.shopsDevicesCountStatistics?.find(s => s.no === 0)?.shops || [],
                          'Bez urządzeń - KRYTYCZNE'
                        )}
                      >
                        {selectedReport.data?.shopsDevicesCountStatistics?.find(s => s.no === 0)?.count || 0}
                      </div>
                      <div className="text-muted">Bez urządzeń</div>
                    </Col>
                    <Col xs={4}>
                      <div 
                        className="text-warning fw-bold clickable-badge"
                        style={{cursor: 'pointer'}}
                        onClick={() => {
                          const shopsWithFewDevices = selectedReport.data?.shopsDevicesCountStatistics
                            ?.filter(s => s.no > 0 && s.no <= 2)
                            ?.flatMap(s => s.shops || []) || [];
                          onHandleShopListModal(
                            'Drogerie z 1-2 urządzeniami - RYZYKO', 
                            shopsWithFewDevices,
                            '1-2 urządzenia - RYZYKO'
                          );
                        }}
                      >
                        {selectedReport.data?.shopsDevicesCountStatistics?.filter(s => s.no > 0 && s.no <= 2).reduce((sum, s) => sum + s.count, 0) || 0}
                      </div>
                      <div className="text-muted">1-2 urządzenia</div>
                    </Col>
                    <Col xs={4}>
                      <div 
                        className="text-success fw-bold clickable-badge"
                        style={{cursor: 'pointer'}}
                        onClick={() => {
                          const shopsWithManyDevices = selectedReport.data?.shopsDevicesCountStatistics
                            ?.filter(s => s.no >= 3)
                            ?.flatMap(s => s.shops || []) || [];
                          onHandleShopListModal(
                            'Drogerie z 3+ urządzeniami - BEZPIECZNE', 
                            shopsWithManyDevices,
                            '3+ urządzenia - BEZPIECZNE'
                          );
                        }}
                      >
                        {selectedReport.data?.shopsDevicesCountStatistics?.filter(s => s.no >= 3).reduce((sum, s) => sum + s.count, 0) || 0}
                      </div>
                      <div className="text-muted">3+ urządzenia</div>
                    </Col>
                  </Row>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card className="shadow-sm report-card">
            <Card.Header className="bg-light">
              <h6 className="mb-0">
                <FontAwesomeIcon icon={faMicrochip} className="me-2" />
                Firmware urządzeń
              </h6>
            </Card.Header>
            <Card.Body>
              <div style={{maxHeight: '350px', overflowY: 'auto'}}>
                {selectedReport.data?.devicesSortedByTypeFwHw
                  ?.slice() // Create a copy to avoid mutating original array
                  ?.sort((a, b) => b.total - a.total) // Sort by total devices descending
                  ?.map((deviceType, index) => (
                  <div key={index} className="mb-3 p-2 border rounded bg-light">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Badge bg="primary" className="px-2 py-1">
                        {deviceType.dev_type}
                      </Badge>
                      <span className="fw-bold">{deviceType.total}</span>
                    </div>
                    
                    {/* All versions, sorted by count descending */}
                    {deviceType.versions
                      ?.slice() // Create a copy to avoid mutating original array
                      ?.sort((a, b) => b.count - a.count) // Sort by count descending
                      ?.map((version, vIndex) => (
                      <div key={vIndex} className="small mb-1">
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">
                            FW {version.fw_version} / HW {version.hw_version}
                          </span>
                          <Badge bg="secondary" className="small">
                            {version.count}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default DeviceHealthSection;